/**
 * The smallest possible router: a view id plus one optional parameter (a product
 * id), mirrored into `location.hash`.
 *
 * The hash is not decoration. Without it the console is a single mutable
 * component tree with no address, so a refresh — or a middle-click, or a link
 * pasted to a colleague — silently lands on the dashboard, and the product you
 * were looking at is gone. `#/product/7` costs one `useEffect` and makes every
 * view addressable.
 *
 * Deliberately still not react-router: there is one nesting level and one
 * parameter, and a real router would be more code than the thing it routes.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

interface Nav {
  view: string;
  param: string | null;
  go: (view: string, param?: string | number) => void;
  openProduct: (id: number) => void;
}

const DEFAULT_VIEW = 'dashboard';

const Ctx = createContext<Nav>({ view: DEFAULT_VIEW, param: null, go: () => {}, openProduct: () => {} });

interface Route {
  view: string;
  param: string | null;
}

/** `#/product/7` → {view:'product', param:'7'}. An empty/garbage hash is the dashboard. */
function parseHash(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean).map(decodeURIComponent);
  if (parts.length === 0) return { view: DEFAULT_VIEW, param: null };
  return { view: parts[0]!, param: parts[1] ?? null };
}

function toHash(r: Route): string {
  const tail = r.param === null ? '' : `/${encodeURIComponent(r.param)}`;
  return `#/${encodeURIComponent(r.view)}${tail}`;
}

export function NavProvider({ children }: { children: (nav: Nav) => ReactNode }) {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));

  // Back/forward, and anyone editing the address bar by hand.
  useEffect(() => {
    const onHash = (): void => setRoute(parseHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const go = useCallback((view: string, param?: string | number) => {
    const next: Route = { view, param: param === undefined ? null : String(param) };
    // Setting the hash fires `hashchange`, which sets the state — but only if the
    // hash actually changed, so re-navigating to where you already are still works.
    const h = toHash(next);
    if (window.location.hash === h) setRoute(next);
    else window.location.hash = h;
  }, []);

  const openProduct = useCallback((id: number) => go('product', id), [go]);

  const nav: Nav = { view: route.view, param: route.param, go, openProduct };
  return <Ctx.Provider value={nav}>{children(nav)}</Ctx.Provider>;
}

export const useNav = (): Nav => useContext(Ctx);
